import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLoader, FiLock } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';

function SecurityLogin() {
  const navigate = useNavigate();
  const { user, loading: authLoading, securityLogin, logout } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationStatus, setLocationStatus] = useState('pending');

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const captureBrowserLocation = async () => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    const capturedAt = new Date().toISOString();

    const getPosition = (options) =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          return reject(new Error('unsupported'));
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, options);
      });

    // Stage 1: Quick high accuracy (GPS).
    // Short 3.5s timeout so phones do not stall indoors; accept cached location up to 5 minutes.
    try {
      const position = await getPosition({
        enableHighAccuracy: true,
        timeout: 3500,
        maximumAge: 300000
      });

      if (position?.coords) {
        setLocationStatus('granted');
        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          browser_timezone: browserTimezone,
          captured_at: capturedAt,
          source: 'browser_gps'
        };
      }
    } catch (gpsErr) {
      // Continue immediately to cellular/Wi-Fi positioning if GPS timed out or satellite signal unavailable
    }

    // Stage 2: Fast Wi-Fi / Cellular low-accuracy positioning.
    // Instantaneous on mobile devices indoors; accept cached location up to 10 minutes.
    try {
      const position = await getPosition({
        enableHighAccuracy: false,
        timeout: 4000,
        maximumAge: 600000
      });

      if (position?.coords) {
        setLocationStatus('granted');
        return {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          browser_timezone: browserTimezone,
          captured_at: capturedAt,
          source: 'browser_network'
        };
      }
    } catch (networkErr) {
      // If browser permission is blocked, denied, or restricted in mobile WebView
    }

    // Stage 3: Seamless IP/Network Fallback.
    // Never block security staff or make them retry repeatedly; pass fallback payload so backend
    // logs location via server-side IP lookup and permits login smoothly.
    setLocationStatus('network_fallback');
    return {
      latitude: null,
      longitude: null,
      accuracy: null,
      browser_timezone: browserTimezone,
      captured_at: capturedAt,
      source: 'ip_fallback',
      reason: 'gps_cellular_unavailable'
    };
  };

  const ensureLocationPermission = async () => {
    return await captureBrowserLocation();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const clientLocation = await ensureLocationPermission();
      const user = await securityLogin(email, password, { client_location: clientLocation });
      if (user.role !== 'admin' && user.role !== 'manager') {
        logout();
        setError('Access denied. This portal is restricted to authorized personnel.');
        return;
      }

      navigate('/security/login-history', { replace: true });
    } catch (loginError) {
      setError(loginError.response?.data?.error || loginError.message || 'Security login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f4f5fb] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md items-center">
        <div className="fleet-card w-full p-6 sm:p-8">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-900 text-white">
              <FiLock className="h-5 w-5" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Security Access</h1>
            <p className="text-sm text-gray-600 mt-1">Authorized personnel with security permission only</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="security-email" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Email
              </label>
              <input
                id="security-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="fleet-input h-11"
                placeholder="Enter authorized email"
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="security-password" className="block text-xs font-semibold uppercase tracking-wide text-gray-600">
                Password
              </label>
              <input
                id="security-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="fleet-input h-11"
                placeholder="Enter password"
              />
            </div>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="flex h-11 w-full items-center justify-center rounded-xl bg-gray-900 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <FiLoader className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Enter Security Portal'
              )}
            </button>

            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 text-xs flex items-center justify-between">
              <span className="text-gray-600 font-medium">Location check</span>
              {locationStatus === 'granted' ? (
                <span className="inline-flex items-center text-emerald-600 font-semibold">
                  <span className="mr-1.5 h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Verified (GPS / Wi-Fi)
                </span>
              ) : locationStatus === 'network_fallback' ? (
                <span className="inline-flex items-center text-blue-600 font-semibold">
                  <span className="mr-1.5 h-2 w-2 rounded-full bg-blue-500"></span>
                  Verified (Network / IP)
                </span>
              ) : (
                <span className="text-gray-500">
                  Auto-verified on sign-in
                </span>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default SecurityLogin;
